/* ChatModelAdapter that talks to the pod agent through the Hub.
 *
 * - Prefers OpenResponses for typed streaming + client-side tools
 * - Sends chat turns only through /api/agent/local-responses
 * - Persists X-MoltPod-Conversation-Id in sessionStorage so refresh keeps the thread
 * - On 401, redirects through the SSO bridge using the server's login_url
 *   except on the home chat, where expired auth is reported inline.
 */

function _safeLoginNextFromBrowser() {
  try {
    const path = window.location.pathname || '/';
    const search = window.location.search || '';
    const safe = path && !path.startsWith('/api/') ? path + search : '/';
    return safe;
  } catch { return '/'; }
}

function _redirectToLogin(serverLoginUrl) {
  const next = _safeLoginNextFromBrowser();
  let target = '/api/auth/start?next=' + encodeURIComponent(next);
  // If the server gave us a login_url, only honor its path; we always
  // override the next= param with the browser-known page.
  if (typeof serverLoginUrl === 'string' && serverLoginUrl) {
    try {
      const u = new URL(serverLoginUrl, window.location.origin);
      if (u.pathname && u.pathname.startsWith('/api/auth/')) {
        target = u.pathname + '?next=' + encodeURIComponent(next);
      }
    } catch {}
  }
  try { window.location.href = target; } catch {}
}

import {
  collectHubPageContext,
  getCurrentHubPageContext,
  setCurrentHubPageContext,
} from './PageContext.js';
import {
  nextPendingFrameDelay,
  pendingResponseText,
} from './PendingWords.js';

const CONV_KEY = 'hub-agent-conversation-id';
const THREAD_KEY = 'hub-agent-visible-thread';
const ASSISTANT_OPEN_KEY = 'hub-assistant-open';
const STREAM_REVEAL_CHARS = 8;
const STREAM_REVEAL_DELAY_MS = 18;
// If no bytes arrive for this long, treat the SSE stream as stalled and trigger
// auto-resume. Must exceed the server keepalive cadence (15s) with margin so a
// merely-quiet-but-alive stream is never mistaken for a dead one.
const STREAM_IDLE_TIMEOUT_MS = 35000;
// Auto-resume polling: after a stall, poll the persisted conversation until the
// finished assistant reply appears (the server keeps working and persists the
// turn even if this socket died).
const STREAM_RESUME_POLL_MS = 2500;
const STREAM_RESUME_MAX_MS = 180000;
const MAX_TOOL_STEPS = 100;
const MAX_REPEATED_TOOL_CALLS = 2;
const MAX_STORED_THREAD_MESSAGES = 18;
const MAX_STORED_THREAD_CHARS = 28000;
const OPENCLAW_TOOL_NAME = 'hub_call_openclaw';
export const AGENT_SESSION_REFRESH_THRESHOLD_SECONDS = 10 * 60;
const MUTATION_INTENT_RE = /\b(update|change|set|edit|add|create|remove|delete|upload|invite|rename|save|correct|replace|modify|patch|send|reschedule|cancel)\b/i;
const OBJECT_HINT_RE = /\b(person|people|contact|profile|event|attendee|guest|photo|message|communication|email|website|site|phone|title|location|address|bio|note|company|role)\b/i;
const READ_ONLY_INTENT_RE = /\b(show|open|view|summarize|summary|explain|who is|what is|list|find|search|look up|tell me|read)\b/i;
const OPENCLAW_NO_CHANGE_RE = /\b(could not|couldn't|can't|cannot|unable|failed|failure|error|not authorized|permission denied|no changes?|nothing (?:changed|updated|to update)|did not (?:update|change|save|delete|remove|create|add)|was not (?:updated|changed|saved|deleted|removed|created|added))\b/i;

const HUB_TOOL_NAMES = new Set([
  'hub_refresh_page',
  'hub_navigate',
  'hub_return_main_menu',
  'hub_open_section',
  'hub_toggle_desktop',
  'hub_open_desktop',
  'hub_close_desktop',
  'hub_event_open_tab',
  'hub_event_curate_room',
  'hub_create_review_item',
  'hub_open_person',
  'hub_update_person',
  'hub_delete_person',
  'hub_update_event',
  'hub_enrich_person_online',
  'hub_run_linkedin_automation',
  'hub_generate_gallery_image',
  'hub_create_scheduled_task',
  'hub_create_standing_order',
  'hub_delete_file_folder',
]);
const LOCAL_ORCHESTRATOR_TOOL_NAMES = new Set([...HUB_TOOL_NAMES, OPENCLAW_TOOL_NAME]);
const scheduledHubActionKeys = new Set();

const HUB_SECTION_PATHS = {
  home: '__home__',
  chats: '/chats',
  dashboard: '__home__',
  chat: '/chats',
  people: '/people',
  events: '/events',
  docs: '/docs',
  files: '/files',
  gallery: '/gallery',
  limits: '/limits',
  scheduled: '/scheduled-tasks',
  'scheduled-tasks': '/scheduled-tasks',
  'scheduled-task': '/scheduled-tasks',
  standing: '/scheduled-tasks',
  'standing-orders': '/scheduled-tasks',
  review: '/review-board',
  'review-board': '/review-board',
  'human-review': '/review-board',
};

let hubStatusCache = null;

async function getHubStatus(signal) {
  if (hubStatusCache) return hubStatusCache;
  try {
    const res = await fetch('/api/status', { credentials: 'same-origin', signal });
    if (!res.ok) return {};
    const status = await res.json();
    hubStatusCache = status && typeof status === 'object' ? status : {};
    return hubStatusCache;
  } catch {
    return {};
  }
}

async function getHubFeatures(signal) {
  const status = await getHubStatus(signal);
  return status?.features && typeof status.features === 'object' ? status.features : {};
}

async function getHubHomePath(signal) {
  const status = await getHubStatus(signal);
  const path = String(status?.home?.path || status?.home_path || '/chats').trim();
  return path.startsWith('/') && !path.startsWith('//') ? path : '/chats';
}

function featureForHubPath(path) {
  const normalized = String(path || '').split(/[?#]/, 1)[0];
  if (/^\/(?:chats|chat)(?:\/|$)/.test(normalized)) return 'chats';
  if (/^\/people(?:\/|$)/.test(normalized)) return 'people';
  if (/^\/events(?:\/|$)/.test(normalized)) return 'events';
  if (/^\/docs(?:\/|$)/.test(normalized)) return 'docs';
  if (/^\/files(?:\/|$)/.test(normalized)) return 'files';
  if (/^\/gallery(?:\/|$)/.test(normalized)) return 'gallery';
  if (/^\/review-board(?:\/|$)/.test(normalized)) return 'human_review';
  if (/^\/(?:scheduled-tasks|standing-orders)(?:\/|$)/.test(normalized)) return 'scheduled_tasks';
  if (/^\/limits(?:\/|$)/.test(normalized)) return 'limits';
  return '';
}

async function isHubPathFeatureEnabled(path, signal) {
  const feature = featureForHubPath(path);
  if (!feature) return true;
  const features = await getHubFeatures(signal);
  return features?.[feature] !== false;
}

async function getHubSectionPath(section, signal) {
  const path = HUB_SECTION_PATHS[section];
  if (path === '__home__') return getHubHomePath(signal);
  return path;
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const hubClientTools = [
  {
    type: 'function',
    name: 'hub_refresh_page',
    description: 'Refresh the current Hub page after the assistant finishes this turn.',
    parameters: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short reason for refreshing the page.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_navigate',
    description: 'Navigate the Hub UI to a safe same-origin Hub path after this turn.',
    parameters: {
      type: 'object',
      properties: {
        path: {
          type: 'string',
          description: 'A Hub path such as /chats, /dashboard, /dashboardv0, /people, /events, /docs, /files, /gallery, /scheduled-tasks, /review-board, /limits, /people/<id>, /events/<id>, /docs/<path>, /files/<id>, or /gallery/<id>.',
        },
      },
      required: ['path'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_return_main_menu',
    description: 'Return the user from this pod Hub to the MoltPod main menu/control app. Use this for requests like "go back to main menu", "open the main menu", "return to MoltPod", or "take me back to app.moltpod.com". This behaves like clicking Hub\'s Back to Main Menu link.',
    parameters: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short reason for returning to the MoltPod main menu.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_open_section',
    description: 'Open a top-level Hub section after this turn.',
    parameters: {
      type: 'object',
      properties: {
        section: {
          type: 'string',
          enum: Object.keys(HUB_SECTION_PATHS),
        },
      },
      required: ['section'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_toggle_desktop',
    description: 'Toggle the pod desktop view in the open Hub assistant FAB. Use this for requests like "show desktop", "hide desktop", "open the desktop", or "toggle desktop". This has no effect if the FAB panel is closed.',
    parameters: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short reason for toggling the desktop.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_open_desktop',
    description: 'Open the pod desktop/VNC viewer in Hub without toggling it closed. Always use this before browser, website, VNC, LinkedIn, or other desktop-visible work.',
    parameters: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short reason for opening the desktop.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_close_desktop',
    description: 'Close the pod desktop/VNC viewer in Hub without toggling it open.',
    parameters: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Short reason for closing the desktop.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_event_open_tab',
    description: 'On an event detail page, switch the current event UI to a tab: overview, guest list, photos, or communications. Always use this local tool for requests like "open guest list", "show guests", "show attendees", "open RSVPs", "show photos", or "open communications"; do not hand off for simple event tab switching.',
    parameters: {
      type: 'object',
      properties: {
        tab: {
          type: 'string',
          enum: ['overview', 'guest_list', 'guests', 'photos', 'communications', 'comms'],
        },
      },
      required: ['tab'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_event_curate_room',
    description: 'On an event detail page, curate the room from fresh event guest data. Use this when the user asks to curate the room, group guests strategically, identify VIPs/connectors/follow-up groups, or organize the guest list by a requested criterion. This shows a curating loader immediately while the backend performs the curation.',
    parameters: {
      type: 'object',
      properties: {
        criteria: {
          type: 'string',
          description: 'The user requested curation criteria, e.g. "VIPs and follow-up priority", "role", "sponsors vs speakers", or "who needs outreach".',
        },
      },
      required: ['criteria'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_event_render_curation',
    description: 'Render backend-produced event room curation results on the current event detail page.',
    parameters: {
      type: 'object',
      properties: {
        criteria: { type: 'string' },
        event_id: { type: 'string' },
        result: { type: 'object' },
        error: { type: 'string' },
      },
      additionalProperties: true,
    },
  },
  {
    type: 'function',
    name: 'hub_delete_file_folder',
    description: 'Delete a folder from Hub Files. Use this only for explicit requests to remove or delete a Hub Files folder; it performs the deletion in Hub and refreshes the Files page afterward.',
    parameters: {
      type: 'object',
      properties: {
        folder: { type: 'string', description: 'The exact Hub Files folder path to delete, for example "Event Covers" or "Clients/Acme".' },
        recursive: { type: 'boolean', description: 'If true, delete files inside the folder too. Default: true for assistant-triggered folder deletes.' },
      },
      required: ['folder'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_create_scheduled_task',
    description: 'Create a Scheduled Task in Hub: a recurring instruction or one-off reminder the agent will run for the user. Provide either `cron` (5-field expression) for recurring, OR `at` (ISO 8601 datetime) for one-shot. Always confirm timing with the user before creating something user-visible.',
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Short human-readable title for the scheduled task.' },
        message: { type: 'string', description: 'The prompt the agent should run when the task fires.' },
        cron: { type: 'string', description: '5-field cron expression, e.g. "0 7 * * *" for 7am daily. Use this OR `at`, not both.' },
        at: { type: 'string', description: 'ISO 8601 datetime for a one-off task, e.g. "2026-05-01T09:00:00-07:00". Use this OR `cron`, not both.' },
        requireApproval: { type: 'boolean', description: 'If true, the task starts paused and a human must enable it in /scheduled-tasks before it runs. Default: false.' },
        sendToEmail: { type: 'string', description: 'Email address to receive the post-run summary. Optional.' },
      },
      required: ['name', 'message'],
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_create_review_item',
    description: 'Create a verified Human Review item in Hub, then open the Human Review page. Use this for requests like "add a sample review item" or "create a review item".',
    parameters: {
      type: 'object',
      properties: {
        summary: { type: 'string', description: 'Short review summary. For sample items, generate a concise demo summary.' },
        kind: { type: 'string', description: 'Review kind/category, e.g. approval, content, data_change, sample.' },
        risk: { type: 'string', enum: ['low', 'med', 'high'], description: 'Review risk level.' },
        details: { type: 'string', description: 'Optional review details/body.' },
        proposed_action: { type: 'object', description: 'Structured action payload for the human to inspect, such as Gmail draft fields or LinkedIn comment text.' },
        context: { type: 'object', description: 'Relevant source context needed to regenerate or execute the request safely.' },
        idempotency_key: { type: 'string', description: 'Optional stable key preventing duplicate execution jobs for the same intended action.' },
        agent: { type: 'string', description: 'Submitting agent/system identity.' },
        sample: { type: 'boolean', description: 'True when the user asked for a sample review item.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_open_person',
    description: 'Navigate to a person profile by exact id or by name/email. Use this for "go to Jane", "open Jane Doe", or "show that person".',
    parameters: {
      type: 'object',
      properties: {
        person_id: { type: 'string', description: 'Known Hub person id.' },
        person: { type: 'string', description: 'Person name or email to resolve through Hub People.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_update_person',
    description: 'Update a Hub person profile through the verified Hub API. Use after the user has provided explicit fields to change. For research/enrichment, hand off first and then call this with a structured patch.',
    parameters: {
      type: 'object',
      properties: {
        person_id: { type: 'string', description: 'Hub person id. If omitted on a person detail page, the current person is used.' },
        person: { type: 'string', description: 'Person name or email when id is unknown.' },
        patch: { type: 'object', description: 'Fields to update on the person.' },
        first_name: { type: 'string' },
        last_name: { type: 'string' },
        display_name: { type: 'string' },
        email: { type: 'string' },
        phone: { type: 'string' },
        company: { type: 'string' },
        title: { type: 'string' },
        website: { type: 'string' },
        linkedin: { type: 'string' },
        twitter: { type: 'string' },
        bio: { type: 'string' },
        address: { type: 'string' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_delete_person',
    description: 'Delete a Hub person through the verified Hub API. Only call with confirmed=true after the user clearly confirms the exact person to delete.',
    parameters: {
      type: 'object',
      properties: {
        person_id: { type: 'string', description: 'Hub person id. If omitted on a person detail page, the current person is used.' },
        person: { type: 'string', description: 'Person name or email when id is unknown.' },
        confirmed: { type: 'boolean', description: 'Must be true only after explicit user confirmation.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_update_event',
    description: 'Update a Hub event through the verified Hub API. Use after the user has provided explicit fields to change. For ambiguous edits, hand off first and then call this with a structured patch.',
    parameters: {
      type: 'object',
      properties: {
        event_id: { type: 'string', description: 'Hub event id. If omitted on an event detail page, the current event is used.' },
        event: { type: 'string', description: 'Event title when id is unknown.' },
        patch: { type: 'object', description: 'Fields to update on the event.' },
        title: { type: 'string' },
        status: { type: 'string' },
        type: { type: 'string' },
        start: { type: 'string' },
        end: { type: 'string' },
        timezone: { type: 'string' },
        location: { type: 'string' },
        virtual_url: { type: 'string' },
        registration_url: { type: 'string' },
        capacity: { type: 'number' },
        description: { type: 'string' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_enrich_person_online',
    description: 'Use OpenClaw to enrich the current or specified person from online sources, then verify/apply any Hub profile changes. Opens the VNC desktop automatically when browser work is needed.',
    parameters: {
      type: 'object',
      properties: {
        person_id: { type: 'string', description: 'Hub person id. If omitted on a person detail page, the current person is used.' },
        person: { type: 'string', description: 'Person name or email when id is unknown.' },
        sources: { type: 'array', items: { type: 'string' }, description: 'Preferred sources, e.g. LinkedIn, Google, company website.' },
        goal: { type: 'string', description: 'What to enrich or verify.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_run_linkedin_automation',
    description: 'Use OpenClaw\'s LinkedIn automation skill through the VNC/browser for the current or specified person. Opens the VNC desktop automatically and streams progress.',
    parameters: {
      type: 'object',
      properties: {
        person_id: { type: 'string', description: 'Hub person id. If omitted on a person detail page, the current person is used.' },
        person: { type: 'string', description: 'Person name or email when id is unknown.' },
        goal: { type: 'string', description: 'LinkedIn automation goal, e.g. find profile, connect, message, enrich.' },
        note: { type: 'string', description: 'Optional connection/message note.' },
      },
      additionalProperties: false,
    },
  },
  {
    type: 'function',
    name: 'hub_generate_gallery_image',
    description: 'Create a verified Gallery image record for an assistant/OpenClaw generated image. If no generated image URL is available yet, create a tracked generation request instead of claiming an image was generated.',
    parameters: {
      type: 'object',
      properties: {
        prompt: { type: 'string', description: 'Image generation prompt requested by the user.' },
        title: { type: 'string', description: 'Gallery title.' },
        url: { type: 'string', description: 'Generated image URL or saved asset URL, if available.' },
        folder: { type: 'string', description: 'Optional Gallery folder.' },
        description: { type: 'string', description: 'Optional description.' },
      },
      required: ['prompt'],
      additionalProperties: false,
    },
  },
];

const localOrchestratorTools = [
  ...hubClientTools,
  {
    type: 'function',
    name: OPENCLAW_TOOL_NAME,
    description: 'Ask the OpenClaw pod agent to handle work that needs pod/runtime/filesystem/agent context. Use mode="desktop" for tasks that must be performed through the pod desktop/VNC, such as opening external websites or operating a browser. The local orchestrator will summarize the result for the user.',
    parameters: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Why this request needs the pod agent.' },
        mode: {
          type: 'string',
          enum: ['pod', 'desktop'],
          description: 'Use "desktop" when OpenClaw should operate the pod desktop/VNC instead of only answering from pod context.',
        },
        desktop_prompt: {
          type: 'string',
          description: 'A direct DESKTOP prompt for OpenClaw describing the browser/VNC action to perform.',
        },
      },
      additionalProperties: false,
    },
  },
];

function delay(ms, signal) {
  if (signal?.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const timeout = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeout);
        resolve();
      }, { once: true });
    }
  });
}

// Thrown when the SSE stream produces no bytes for STREAM_IDLE_TIMEOUT_MS.
// Distinct name so runResponses can tell a stall apart from a hard error and
// switch into auto-resume instead of asking the user to resend.
class StreamStalledError extends Error {
  constructor(message = 'stream stalled') {
    super(message);
    this.name = 'StreamStalledError';
  }
}

// Read one chunk from a stream reader, but reject with StreamStalledError if no
// chunk (and no keepalive byte) arrives within timeoutMs. Only one read is ever
// outstanding at a time; on stall the caller cancels the reader.
function readWithIdleTimeout(reader, timeoutMs, abortSignal) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn, arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (abortSignal) abortSignal.removeEventListener('abort', onAbort);
      fn(arg);
    };
    const timer = setTimeout(() => settle(reject, new StreamStalledError()), timeoutMs);
    const onAbort = () => {
      const err = new Error('aborted');
      err.name = 'AbortError';
      settle(reject, err);
    };
    if (abortSignal) {
      if (abortSignal.aborted) return onAbort();
      abortSignal.addEventListener('abort', onAbort, { once: true });
    }
    reader.read().then(
      (result) => settle(resolve, result),
      (err) => settle(reject, err),
    );
  });
}

function lastAssistantText(messages) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.role === 'assistant' && typeof m.content === 'string' && m.content.trim()) {
      return m.content;
    }
  }
  return '';
}

// Auto-resume a stalled turn. The server persists the finished assistant reply
// to the hub thread regardless of whether this socket survived, so we poll the
// conversation until the reply lands instead of forcing a manual resend. Yields
// interim "Reconnecting…" frames and returns { text } on success, or null if we
// could not recover within STREAM_RESUME_MAX_MS.
async function* resumeStalledResponse(conversationId, acc, abortSignal) {
  const convId = responseConversationIdForHandoff(conversationId) || String(conversationId || '').trim();
  if (!convId) return null;

  const first = await fetchHistoryMessages(convId, abortSignal);
  if (abortSignal?.aborted) return null;
  const baseline = first.filter((m) => m.role === 'assistant').length;

  // Missed-terminator case: the reply was already fully persisted before we
  // noticed the stall. Recover it directly when it clearly extends what we have
  // already shown (guarded so we never surface a prior turn's answer as this
  // one when nothing streamed yet).
  const accTrim = acc.trim();
  if (accTrim) {
    const already = lastAssistantText(first);
    if (already && already.length > acc.length && already.includes(accTrim.slice(0, 40))) {
      return { text: already };
    }
  }

  const deadline = Date.now() + STREAM_RESUME_MAX_MS;
  let dots = 1;
  while (Date.now() < deadline) {
    if (abortSignal?.aborted) return null;
    await delay(STREAM_RESUME_POLL_MS, abortSignal);
    if (abortSignal?.aborted) return null;
    const msgs = await fetchHistoryMessages(convId, abortSignal);
    const assistants = msgs.filter((m) => m.role === 'assistant');
    if (assistants.length > baseline) {
      const text = lastAssistantText(msgs);
      if (text && text.trim()) return { text };
    }
    dots = dots >= 3 ? 1 : dots + 1;
    yield { content: [{ type: 'text', text: `${acc ? acc + '\n\n' : ''}_Reconnecting${'.'.repeat(dots)}_`.trim() }] };
  }
  return null;
}

async function* withPendingResponseFrames(source, abortSignal) {
  const iterator = source[Symbol.asyncIterator]();
  let frameIndex = 0;
  let next = iterator.next();

  yield { content: [{ type: 'text', text: pendingResponseText(frameIndex) }] };
  frameIndex += 1;

  try {
    while (true) {
      if (abortSignal?.aborted) return;
      const result = await Promise.race([
        next.then((value) => ({ type: 'next', value })),
        delay(nextPendingFrameDelay(), abortSignal).then(() => ({ type: 'tick' })),
      ]);

      if (result.type === 'tick') {
        if (abortSignal?.aborted) return;
        yield { content: [{ type: 'text', text: pendingResponseText(frameIndex) }] };
        frameIndex += 1;
        continue;
      }

      if (result.value.done) return;
      yield result.value.value;
      break;
    }

    while (true) {
      if (abortSignal?.aborted) return;
      next = iterator.next();
      const result = await next;
      if (result.done) return;
      yield result.value;
    }
  } finally {
    try { await iterator.return?.(); } catch {}
  }
}

export function getStoredConversationId() {
  try { return sessionStorage.getItem(CONV_KEY); } catch { return null; }
}

export function setStoredConversationId(id) {
  const previousId = getStoredConversationId();
  try {
    if (id) sessionStorage.setItem(CONV_KEY, id);
    else sessionStorage.removeItem(CONV_KEY);
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent('hub:assistant-conversation-changed', {
      detail: { conversationId: id || null, previousConversationId: previousId || null },
    }));
  } catch {}
}

function normalizeThreadMessage(message) {
  const role = ['user', 'assistant'].includes(message?.role) ? message.role : null;
  const content = flattenContent(message?.content).trim();
  if (!role || !content) return null;
  return { role, content };
}

function storedThreadMessages() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(THREAD_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeThreadMessage).filter(Boolean);
  } catch {
    return [];
  }
}

function trimThreadMessages(messages) {
  const normalized = messages.map(normalizeThreadMessage).filter(Boolean);
  const kept = [];
  let chars = 0;
  for (let i = normalized.length - 1; i >= 0; i -= 1) {
    const next = normalized[i];
    const nextChars = next.content.length;
    if (kept.length >= MAX_STORED_THREAD_MESSAGES || chars + nextChars > MAX_STORED_THREAD_CHARS) break;
    kept.unshift(next);
    chars += nextChars;
  }
  return kept;
}

export function rememberThreadMessages(messages) {
  try {
    const compact = trimThreadMessages(messages);
    if (compact.length) sessionStorage.setItem(THREAD_KEY, JSON.stringify(compact));
    else sessionStorage.removeItem(THREAD_KEY);
  } catch {}
}

export function clearStoredThreadMessages() {
  try { sessionStorage.removeItem(THREAD_KEY); } catch {}
}

function inputHasPriorVisibleContext(input) {
  const visible = input
    .filter((item) => item?.type === 'message' && ['user', 'assistant'].includes(item.role))
    .map(normalizeThreadMessage)
    .filter(Boolean);
  return visible.slice(0, -1).some((item) => item.role === 'assistant' || item.role === 'user');
}

function mergeStoredThreadIntoInput(input) {
  if (inputHasPriorVisibleContext(input)) return input;
  const stored = storedThreadMessages();
  if (!stored.length) return input;
  const latest = input.map(normalizeThreadMessage).filter(Boolean).at(-1);
  const prior = latest
    ? stored.filter((item) => !(item.role === latest.role && item.content === latest.content))
    : stored;
  return [
    ...prior.map((item) => ({ type: 'message', role: item.role, content: item.content })),
    ...input,
  ];
}

function rememberAssistantTurn(input, assistantText) {
  const assistant = String(assistantText || '').trim();
  const visible = input
    .filter((item) => item?.type === 'message' && ['user', 'assistant'].includes(item.role))
    .map(normalizeThreadMessage)
    .filter(Boolean);
  rememberThreadMessages(assistant
    ? [...visible, { role: 'assistant', content: assistant }]
    : visible);
}

function flattenContent(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((p) => (p && p.type === 'text' ? p.text || '' : ''))
      .filter(Boolean)
      .join('');
  }
  return '';
}

function latestUserTextFromInput(input) {
  if (!Array.isArray(input)) return '';
  for (let i = input.length - 1; i >= 0; i -= 1) {
    const item = input[i];
    if (item?.type === 'message' && item.role === 'user') {
      return flattenContent(item.content);
    }
  }
  return '';
}

function buildDesktopTaskInstructions({ pageContext, desktopPrompt, userText }) {
  const pagePrompt = pageContext?.prompt && isContextForCurrentPage(pageContext)
    ? pageContext.prompt
    : '';
  return [
    'DESKTOP TASK.',
    'You are OpenClaw operating the user\'s pod desktop through VNC/noVNC.',
    'Use the graphical desktop and browser when needed. Do not only describe steps if you can act.',
    'If the user asked to open, visit, browse to, search, sign in to, inspect, or operate an external website, perform that action on the pod desktop.',
    'Keep the browser/desktop in the resulting state so the user can see it in Hub.',
    'Report concise progress and the final visible result back to the Hub assistant.',
    '',
    `User request: ${userText || desktopPrompt || '(none)'}`,
    `Desktop prompt: ${desktopPrompt || userText || '(none)'}`,
    pagePrompt ? `Hub page context:\n${pagePrompt}` : '',
  ].filter(Boolean).join('\n');
}

function buildDesktopTaskInput(input, desktopPrompt) {
  const prompt = String(desktopPrompt || '').trim();
  if (!prompt) return input;
  return [
    ...(Array.isArray(input) ? input : []),
    {
      type: 'message',
      role: 'user',
      content: `DESKTOP TASK PROMPT:\n${prompt}`,
    },
  ];
}

export async function fetchHistoryMessages(conversationId, signal) {
  if (!conversationId) return [];
  try {
    const r = await fetch(`/api/agent/local-responses/conversations/${encodeURIComponent(conversationId)}`, {
      credentials: 'same-origin',
      signal,
    });
    if (!r.ok) return [];
    const data = await r.json().catch(() => ({}));
    const msgs = Array.isArray(data?.messages) ? data.messages : [];
    return msgs
      .filter((m) => m?.role === 'user' || m?.role === 'assistant')
      .map((m) => ({
        role: m.role,
        content: textFromParts(m),
      }));
  } catch {
    return [];
  }
}

export async function listConversations(signal) {
  try {
    const r = await fetch('/api/agent/local-responses/conversations', {
      credentials: 'same-origin',
      signal,
    });
    if (!r.ok) return [];
    const data = await r.json().catch(() => ({}));
    const list = Array.isArray(data?.conversations) ? data.conversations : [];
    return list
      .filter((item) => item?.id)
      .map((item) => ({
        id: String(item.id),
        title: String(item.title || item.summary || 'New conversation'),
        updated_at: item.updated_at || item.created_at || '',
        message_count: Number(item.message_count || 0),
      }));
  } catch {
    return [];
  }
}

export async function createConversation(signal) {
  try {
    const r = await fetch('/api/agent/local-responses/conversations', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      signal,
    });
    if (!r.ok) return null;
    const data = await r.json().catch(() => ({}));
    const item = data?.conversation;
    if (!item?.id) return null;
    return {
      id: String(item.id),
      title: String(item.title || item.summary || 'New conversation'),
      updated_at: item.updated_at || item.created_at || '',
      message_count: Number(item.message_count || 0),
    };
  } catch {
    return null;
  }
}

export async function deleteConversation(id, signal) {
  if (!id) return false;
  try {
    const r = await fetch(`/api/agent/local-responses/conversations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      signal,
    });
    return r.ok || r.status === 204;
  } catch {
    return false;
  }
}

function isContextForCurrentPage(context) {
  if (context?.embedded === true || context?.source === 'host') return true;
  if (!context?.source_url || typeof window === 'undefined') return false;
  try {
    return new URL(context.source_url).pathname === window.location.pathname;
  } catch {
    return false;
  }
}

function textFromParts(m) {
  if (Array.isArray(m?.parts) && m.parts.length) {
    return m.parts
      .map((p) => {
        if (!p) return '';
        if (p.type === 'text') return p.text || '';
        if (p.type === 'image_url' && p.image_url?.url) return `[image: ${p.image_url.url}]`;
        return `[${p.label || 'unsupported'}: ${p.original_type || p.type || 'unknown'}]`;
      })
      .filter(Boolean)
      .join('\n');
  }
  return typeof m?.content === 'string' ? m.content : '';
}

function messagesToResponseInput(messages) {
  const input = [];
  for (const m of messages) {
    const role = ['system', 'developer', 'user', 'assistant'].includes(m.role) ? m.role : 'user';
    input.push({
      type: 'message',
      role,
      content: flattenContent(m.content),
    });
  }
  return input;
}

function parseToolArguments(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(String(raw)); } catch { return {}; }
}

function stableToolArguments(raw) {
  const parsed = parseToolArguments(raw);
  const normalize = (value) => {
    if (Array.isArray(value)) return value.map(normalize);
    if (value && typeof value === 'object') {
      return Object.keys(value).sort().reduce((acc, key) => {
        acc[key] = normalize(value[key]);
        return acc;
      }, {});
    }
    return value;
  };
  try {
    return JSON.stringify(normalize(parsed));
  } catch {
    return String(raw || '');
  }
}

function terminalHubToolText(output) {
  if (!output || typeof output !== 'object') return '';
  if (output.action === 'event_room_curated') {
    return String(output.summary || 'Done. I rendered the curated guest groups.');
  }
  if (output.action === 'event_room_curation_failed') {
    return String(output.error || 'The room curation could not finish.');
  }
  return '';
}

function safeHubPath(raw) {
  const value = String(raw || '').trim();
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  let url;
  try { url = new URL(value, window.location.origin); } catch { return null; }
  if (url.origin !== window.location.origin) return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  const allowed = [
    /^\/$/,
    /^\/dashboard\/?$/,
    /^\/dashboardv0\/?$/,
    /^\/chats\/?$/,
    /^\/chat\/?$/,
    /^\/people\/?$/,
    /^\/people\/[^/]+\/?$/,
    /^\/events\/?$/,
    /^\/events\/[^/]+\/?$/,
    /^\/docs\/?$/,
    /^\/docs\/.+$/,
    /^\/files\/?$/,
    /^\/files\/[^/]+\/?$/,
    /^\/gallery\/?$/,
    /^\/gallery\/[^/]+\/?$/,
    /^\/review-board\/?$/,
    /^\/standing-orders\/?$/,
    /^\/scheduled-tasks\/?$/,
    /^\/limits\/?$/,
  ];
  if (!allowed.some((re) => re.test(path))) return null;
  return path;
}

function normalizeObjectLookup(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/\.[a-z0-9]{1,8}$/i, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function objectLookupKeys(item, fields) {
  const keys = new Set();
  for (const field of ['id', ...fields]) {
    const value = item?.[field];
    if (value === undefined || value === null) continue;
    const raw = String(value).trim();
    if (!raw) continue;
    keys.add(normalizeObjectLookup(raw));
  }
  return keys;
}

async function fetchHubList(path, signal) {
  try {
    const res = await fetch(path, { credentials: 'same-origin', signal });
    if (!res.ok) return [];
    const data = await res.json();
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  } catch {
    return [];
  }
}

async function resolveHubObjectPath(path, signal) {
  const match = String(path || '').match(/^\/(people|events|files|gallery)\/([^/?#]+)\/?([?#].*)?$/);
  if (!match) return path;

  const [, section, rawSegment, suffix = ''] = match;
  let segment;
  try { segment = decodeURIComponent(rawSegment); } catch { segment = rawSegment; }
  const wanted = normalizeObjectLookup(segment);
  if (!wanted) return path;

  const specs = {
    people: {
      api: '/api/people',
      fields: ['display_name', 'name', 'username', 'email', 'first_name', 'last_name'],
      combined: (item) => [item?.first_name, item?.last_name].filter(Boolean).join(' '),
    },
    events: {
      api: '/api/events',
      fields: ['title', 'name', 'location'],
    },
    files: {
      api: '/api/files',
      fields: ['title', 'filename', 'name'],
    },
    gallery: {
      api: '/api/gallery',
      fields: ['title', 'filename', 'name'],
    },
  };
  const spec = specs[section];
  if (!spec) return path;

  const items = await fetchHubList(spec.api, signal);
  const exactMatches = [];
  const looseMatches = [];
  for (const item of items) {
    const id = item?.id;
    if (id === undefined || id === null || String(id).trim() === '') continue;
    const keys = objectLookupKeys(item, spec.fields);
    if (spec.combined) {
      const combined = normalizeObjectLookup(spec.combined(item));
      if (combined) keys.add(combined);
    }
    if (keys.has(wanted)) {
      exactMatches.push(item);
      continue;
    }
    for (const key of keys) {
      if (key && (key.includes(wanted) || wanted.includes(key))) {
        looseMatches.push(item);
        break;
      }
    }
  }

  const matchItem = exactMatches.length === 1 ? exactMatches[0] : (exactMatches.length ? null : (looseMatches.length === 1 ? looseMatches[0] : null));
  if (!matchItem) return path;
  return `/${section}/${encodeURIComponent(String(matchItem.id))}${suffix}`;
}

async function resolveSafeHubPath(raw, signal) {
  const path = safeHubPath(raw);
  if (!path || !(await isHubPathFeatureEnabled(path, signal))) return null;
  const resolved = safeHubPath(await resolveHubObjectPath(path, signal));
  if (!resolved || !(await isHubPathFeatureEnabled(resolved, signal))) return null;
  return resolved;
}

function rememberAssistantOpenForNavigation() {
  try {
    window.sessionStorage.setItem(ASSISTANT_OPEN_KEY, '1');
  } catch {}
}

function startSoftNavigation(path) {
  try {
    if (typeof window.hubSoftNavigateTo !== 'function') return false;
    return window.hubSoftNavigateTo(path, { source: 'assistant' }) === true;
  } catch {
    return false;
  }
}

function startSoftRefresh() {
  try {
    // Funnel through the shared refresh coordinator (hub-events.js) when
    // present — it coalesces bursts and holds refreshes while an agent run
    // is active, so repeated refresh actions collapse into one.
    if (typeof window.hubRequestRefresh === 'function') {
      return window.hubRequestRefresh({ kind: 'assistant', op: 'refresh' }) === true;
    }
    if (typeof window.hubSoftReload !== 'function') return false;
    return window.hubSoftReload({ source: 'assistant', fallback: false }) === true;
  } catch {
    return false;
  }
}

function scheduleHubAction(action) {
  if (typeof window === 'undefined') return;
  if (!action?.type) return;
  const key = JSON.stringify(action);
  if (action.type !== 'desktop_toggle') {
    if (scheduledHubActionKeys.has(key)) return;
    scheduledHubActionKeys.add(key);
  }
  try {
    window.dispatchEvent(new CustomEvent('hub:assistant-action', { detail: action }));
  } catch {}
  window.setTimeout(() => {
    try {
      if (action.type === 'refresh') {
        rememberAssistantOpenForNavigation();
        // Soft only — never window.location.reload() from agent actions.
        // A hard reload remounts the assistant mid-conversation and can
        // loop when completion events re-fire after the remount.
        if (!startSoftRefresh()) console.warn('hub refresh skipped: no soft-reload helper on this page');
      }
      if (action.type === 'navigate') {
        rememberAssistantOpenForNavigation();
        if (!startSoftNavigation(action.path)) window.location.assign(action.path);
      }
      if (action.type === 'return_main_menu') {
        window.location.assign('/api/auth/return-to-control?next=%2F');
      }
    } catch {}
  }, 350);
}

function dispatchAssistantRunState(running) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('hub:assistant-run-state', {
      detail: { running: Boolean(running) },
    }));
  } catch {}
}

function dispatchOpenClawState(active, detail = {}) {
  if (typeof window === 'undefined') return;
  if (active && (detail?.desktop_required || String(detail?.mode || '').toLowerCase() === 'desktop')) {
    scheduleHubAction({ type: 'desktop_open', reason: detail?.label || 'OpenClaw is using the pod desktop.' });
  }
  try {
    window.dispatchEvent(new CustomEvent('hub:assistant-openclaw-state', {
      detail: { ...detail, active: Boolean(active) },
    }));
  } catch {}
}

function isPlannerMissOpenClawCall(call) {
  if (String(call?.name || '') !== OPENCLAW_TOOL_NAME) return false;
  const args = parseToolArguments(call?.arguments);
  const reason = String(args.reason || '').toLowerCase();
  return reason.includes('local planner did not return a usable response');
}

function isEventDetailPage() {
  if (typeof window === 'undefined') return false;
  return /^\/events\/[^/]+\/?$/.test(window.location.pathname || '');
}

function currentEventIdFromLocation() {
  if (typeof window === 'undefined') return '';
  const match = (window.location.pathname || '').match(/^\/events\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : '';
}

function eventAssistantBridge() {
  if (typeof window === 'undefined') return null;
  const bridge = window.hubEventAssistant;
  return bridge && typeof bridge === 'object' ? bridge : null;
}

function currentHubPath(pageContext) {
  if (pageContext?.source_url) {
    try {
      const url = new URL(pageContext.source_url, window.location.origin);
      return url.pathname || '';
    } catch {}
  }
  try {
    return window.location.pathname || '';
  } catch {
    return '';
  }
}

function objectContextKind(pageContext) {
  const kind = String(pageContext?.kind || '').toLowerCase();
  if (kind === 'person' || kind === 'people') return kind;
  if (kind === 'event' || kind === 'events') return kind;
  const path = currentHubPath(pageContext);
  if (/^\/people\/[^/]+\/?$/.test(path)) return 'person';
  if (/^\/people\/?$/.test(path)) return 'people';
  if (/^\/events\/[^/]+\/?$/.test(path)) return 'event';
  if (/^\/events\/?$/.test(path)) return 'events';
  return '';
}

function deleteNavigationForMutation(userText, pageContext) {
  const text = String(userText || '').toLowerCase();
  const kind = objectContextKind(pageContext);
  if (kind === 'person' && /\b(delete|remove)\b.*\b(this person|this contact|person|contact|them|him|her)\b/.test(text)) {
    return { type: 'navigate', path: '/people' };
  }
  if (kind === 'event' && /\b(delete|remove|cancel)\b.*\b(this event|event)\b/.test(text)) {
    return { type: 'navigate', path: '/events' };
  }
  return null;
}

function mutationRefreshActionForOpenClaw({ input, pageContext, text, calls }) {
  const kind = objectContextKind(pageContext);
  if (!kind) return null;

  const userText = latestUserTextFromInput(input);
  const combined = `${userText}\n${text || ''}`;
  const hasMutationIntent = MUTATION_INTENT_RE.test(userText);
  const hasObjectHint = OBJECT_HINT_RE.test(userText) || kind === 'person' || kind === 'event';
  if (!hasMutationIntent || !hasObjectHint) return null;
  if (READ_ONLY_INTENT_RE.test(userText) && !MUTATION_INTENT_RE.test(userText)) return null;
  if (OPENCLAW_NO_CHANGE_RE.test(combined)) return null;
  if (!text && (!Array.isArray(calls) || !calls.length)) return null;

  return deleteNavigationForMutation(userText, pageContext) || { type: 'refresh' };
}

function normalizeEventTab(tab) {
  const value = String(tab || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const aliases = {
    overview: 'overview',
    guest: 'guests',
    guests: 'guests',
    guestlist: 'guests',
    guest_list: 'guests',
    attendee: 'guests',
    attendees: 'guests',
    attendee_list: 'guests',
    rsvp: 'guests',
    rsvps: 'guests',
    invitee: 'guests',
    invitees: 'guests',
    photos: 'photos',
    photo: 'photos',
    communications: 'communications',
    communication: 'communications',
    comms: 'communications',
  };
  return aliases[value] || null;
}

async function fetchEventRoomSnapshot(eventId, abortSignal) {
  const id = String(eventId || '').trim();
  if (!id) return null;
  const encoded = encodeURIComponent(id);
  const [event, attendees, messages] = await Promise.all([
    fetchJson(`/api/events/${encoded}`, { signal: abortSignal }).catch(() => null),
    fetchJson(`/api/events/${encoded}/attendees`, { signal: abortSignal }).catch(() => []),
    fetchJson(`/api/events/${encoded}/messages`, { signal: abortSignal }).catch(() => []),
  ]);
  return {
    event_id: id,
    event: event || null,
    attendees: Array.isArray(attendees) ? attendees : [],
    messages: Array.isArray(messages) ? messages : [],
    active_tab: 'guests',
  };
}

function trimCurationSnapshot(snapshot) {
  const event = snapshot?.event || {};
  const attendees = Array.isArray(snapshot?.attendees) ? snapshot.attendees : [];
  const messages = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  return {
    event_id: snapshot?.event_id || event.id || '',
    event: {
      id: event.id,
      title: event.title || event.name,
      start: event.start || event.date,
      end: event.end,
      location: event.location,
      type: event.type,
      status: event.status,
      capacity: event.capacity,
      description: event.description,
      labels: event.labels,
    },
    attendees: attendees.slice(0, 250).map((a) => ({
      id: a.id,
      person: a.person || a.person_id,
      name: a.name || a.display_name,
      email: a.email,
      status: a.status,
      role: a.role,
      company: a.company || a.organization,
      labels: a.labels,
      notes: a.notes,
    })),
    messages: messages.slice(0, 40).map((m) => ({
      subject: m.subject,
      platform: m.platform,
      date: m.date || m.created,
      body: m.body || m.text || m.content,
      recipients: Array.isArray(m.recipients) ? m.recipients.length : undefined,
    })),
  };
}

function extractJsonObjectFromText(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidates = [];
  if (fenced?.[1]) candidates.push(fenced[1].trim());
  candidates.push(raw);
  const first = raw.indexOf('{');
  const last = raw.lastIndexOf('}');
  if (first !== -1 && last > first) candidates.push(raw.slice(first, last + 1));
  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {}
  }
  return null;
}

function normalizeCurationResult(text, criteria) {
  const parsed = extractJsonObjectFromText(text);
  if (parsed) {
    return {
      title: parsed.title || 'Curated guest list',
      criteria: parsed.criteria || criteria,
      summary: parsed.summary || parsed.rationale || '',
      groups: Array.isArray(parsed.groups) ? parsed.groups : [],
      raw_text: text,
    };
  }
  return {
    title: 'Curated guest view',
    criteria,
    summary: '',
    groups: [],
    raw_text: text || 'No curation text was returned.',
  };
}

function eventCurationInstructions(criteria, snapshot) {
  return [
    'You are an event curation agent helping a live event operator.',
    'Use the attendee data and event context provided by Hub.',
    'Do not invent attendees. Only group guests from the provided attendee list.',
    'Return only JSON. Do not wrap it in markdown.',
    'Schema:',
    '{"title":"string","criteria":"string","summary":"string","groups":[{"title":"string","rationale":"string","members":[{"name":"string","email":"string","status":"string","role":"string","reason":"string"}]}]}',
    'Keep group names demo-friendly and operational: VIPs, connectors, speakers, sponsors, follow-up, arrivals, room anchors, or the user requested grouping.',
    `Requested criteria: ${criteria || 'curate the room strategically'}`,
    `Event: ${snapshot?.event?.title || snapshot?.event_id || 'current event'}`,
  ].join('\n');
}

async function runEventOpenTabTool(args) {
  if (!isEventDetailPage()) {
    return { ok: false, error: 'Open an event detail page before switching event tabs.' };
  }
  const tab = normalizeEventTab(args.tab);
  if (!tab) return { ok: false, error: 'Unknown event tab.' };
  const bridge = eventAssistantBridge();
  const result = bridge?.openTab
    ? bridge.openTab(tab)
    : { ok: false, event_id: currentEventIdFromLocation() };
  const needsScheduledSideEffect = !result?.ok;
  if (needsScheduledSideEffect) scheduleHubAction({ type: 'event_open_tab', tab, event_id: currentEventIdFromLocation() });
  return {
    ok: Boolean(result?.ok) || !bridge?.openTab,
    action: 'event_tab_opened',
    tab,
    event_id: result?.event_id || null,
    ...(needsScheduledSideEffect ? {
      hub_action: { type: 'event_open_tab', tab, event_id: result?.event_id || currentEventIdFromLocation() },
    } : {}),
  };
}

async function runEventCurateRoomTool(args, context = {}) {
  const criteria = String(args.criteria || '').trim() || 'Curate the room strategically.';
  if (!isEventDetailPage()) {
    return { ok: false, error: 'Open an event detail page before curating the room.' };
  }
  const bridge = eventAssistantBridge();
  const eventId = currentEventIdFromLocation();
  if (!bridge?.startRoomCuration) {
    scheduleHubAction({ type: 'event_room_curation_start', criteria, event_id: eventId });
  }
  let snapshot = null;
  try {
    if (bridge?.startRoomCuration) {
      snapshot = await bridge.startRoomCuration({ criteria });
    } else {
      snapshot = await fetchEventRoomSnapshot(eventId, context.abortSignal);
    }
  } catch (err) {
    bridge.failRoomCuration?.('I could not reload the guest list before curating the room.');
    scheduleHubAction({
      type: 'event_room_curation_fail',
      event_id: eventId,
      error: 'I could not reload the guest list before curating the room.',
    });
    return { ok: false, error: 'Could not reload event guests before curation.' };
  }
  if (!snapshot) {
    scheduleHubAction({
      type: 'event_room_curation_fail',
      event_id: eventId,
      error: 'I could not load the event attendee snapshot before curating the room.',
    });
    return { ok: false, error: 'Could not load event attendees before curation.' };
  }

  return {
    ok: true,
    action: 'event_room_curation_snapshot',
    criteria,
    event_id: snapshot?.event_id || null,
    snapshot: trimCurationSnapshot(snapshot),
  };
}

async function runEventRenderCurationTool(args) {
  if (!isEventDetailPage()) {
    return { ok: false, error: 'Open an event detail page before rendering room curation.' };
  }
  const bridge = eventAssistantBridge();
  if (args?.error) {
    if (bridge?.failRoomCuration) bridge.failRoomCuration(String(args.error));
    else scheduleHubAction({ type: 'event_room_curation_fail', event_id: args.event_id || currentEventIdFromLocation(), error: String(args.error) });
    return { ok: false, action: 'event_room_curation_failed', error: String(args.error) };
  }
  const result = args?.result && typeof args.result === 'object' ? args.result : null;
  if (!result) {
    if (bridge?.failRoomCuration) bridge.failRoomCuration('The room curation did not return grouped guest results.');
    else scheduleHubAction({
      type: 'event_room_curation_fail',
      event_id: args.event_id || currentEventIdFromLocation(),
      error: 'The room curation did not return grouped guest results.',
    });
    return { ok: false, action: 'event_room_curation_failed', error: 'Missing curation result.' };
  }
  try {
    if (bridge?.renderRoomCuration) bridge.renderRoomCuration(result);
    else scheduleHubAction({
      type: 'event_room_curation_render',
      criteria: args.criteria || result.criteria || '',
      event_id: args.event_id || currentEventIdFromLocation(),
      result,
    });
    return {
      ok: true,
      action: 'event_room_curated',
      criteria: args.criteria || result.criteria || '',
      event_id: args.event_id || null,
      groups: Array.isArray(result.groups) ? result.groups.length : 0,
      summary: result.summary || '',
    };
  } catch (err) {
    bridge.failRoomCuration?.(err?.message || 'The room curation could not render.');
    scheduleHubAction({
      type: 'event_room_curation_fail',
      event_id: args.event_id || currentEventIdFromLocation(),
      error: err?.message || 'The room curation could not render.',
    });
    return { ok: false, action: 'event_room_curation_failed', error: err?.message || 'Render failed.' };
  }
}

async function fetchJson(path, options = {}) {
  const r = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data?.error || `HTTP ${r.status}`);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

function currentObjectId(section, pageContext) {
  const path = currentHubPath(pageContext);
  const match = String(path || '').match(new RegExp(`^/${section}/([^/?#]+)/?$`));
  if (!match) return '';
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

async function resolveHubObjectId(section, raw, pageContext, signal) {
  const explicit = String(raw || '').trim();
  if (explicit) {
    if (/^\d+$/.test(explicit)) return explicit;
    const resolved = await resolveSafeHubPath(`/${section}/${encodeURIComponent(explicit)}`, signal);
    const match = resolved && resolved.match(new RegExp(`^/${section}/([^/?#]+)/?$`));
    if (match) {
      try { return decodeURIComponent(match[1]); } catch { return match[1]; }
    }
  }
  return currentObjectId(section, pageContext);
}

function patchFromArgs(args, fields) {
  const patch = args?.patch && typeof args.patch === 'object' && !Array.isArray(args.patch)
    ? { ...args.patch }
    : {};
  for (const field of fields) {
    if (Object.prototype.hasOwnProperty.call(args || {}, field)) {
      patch[field] = args[field];
    }
  }
  for (const key of Object.keys(patch)) {
    if (patch[key] === undefined) delete patch[key];
  }
  return patch;
}

async function runCreateReviewItemTool(args, context = {}) {
  const sample = args.sample === true || !String(args.summary || '').trim();
  const body = {
    summary: String(args.summary || (sample ? 'Sample review item from the Hub assistant' : '')).trim(),
    kind: String(args.kind || (sample ? 'sample' : 'approval')).trim(),
    risk: String(args.risk || 'low').trim().toLowerCase(),
    details: String(args.details || (sample ? 'This sample item verifies the Human Review queue from the assistant.' : '')).trim(),
    proposed_action: args.proposed_action && typeof args.proposed_action === 'object' ? args.proposed_action : null,
    context: args.context && typeof args.context === 'object' ? args.context : null,
    idempotency_key: String(args.idempotency_key || '').trim() || null,
    agent: String(args.agent || '').trim() || undefined,
  };
  if (!body.summary || !body.kind) return { ok: false, action: 'review_item_create_failed', error: 'summary and kind are required.' };
  try {
    const created = await fetchJson('/api/reviews', {
      method: 'POST',
      body: JSON.stringify(body),
      signal: context.abortSignal,
    });
    return {
      ok: true,
      action: 'review_item_created',
      id: created.id,
      summary: created.summary || body.summary,
      kind: created.kind || body.kind,
      risk: created.risk || body.risk,
      verified: true,
      hub_action: { type: 'navigate', path: '/review-board' },
    };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { ok: false, action: 'review_item_create_failed', error: err.message || 'Could not create the review item.' };
  }
}

async function runOpenPersonTool(args, context = {}) {
  const id = await resolveHubObjectId('people', args.person_id || args.id || args.person, context.pageContext, context.abortSignal);
  const target = id || String(args.person || '').trim();
  if (!target) return { ok: false, action: 'person_open_failed', error: 'Person name or id is required.' };
  const path = await resolveSafeHubPath(`/people/${encodeURIComponent(target)}`, context.abortSignal);
  if (!path || !/^\/people\/[^/]+\/?$/.test(path)) {
    return { ok: false, action: 'person_open_failed', error: `I could not find exactly one matching person for "${target}".` };
  }
  return {
    ok: true,
    action: 'person_opened',
    path,
    person_id: path.split('/').filter(Boolean)[1] || target,
    verified: true,
    hub_action: { type: 'navigate', path },
  };
}

async function runUpdatePersonTool(args, context = {}) {
  const id = await resolveHubObjectId('people', args.person_id || args.id || args.person, context.pageContext, context.abortSignal);
  if (!id) return { ok: false, action: 'person_update_failed', error: 'Open a person page or provide a person id/name before updating.' };
  const patch = patchFromArgs(args, [
    'first_name', 'last_name', 'display_name', 'email', 'phone',
    'company', 'title', 'website', 'linkedin', 'twitter', 'bio', 'address',
  ]);
  if (!Object.keys(patch).length) return { ok: false, action: 'person_update_failed', error: 'No person fields were provided to update.' };
  try {
    const updated = await fetchJson(`/api/people/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
      signal: context.abortSignal,
    });
    const verified = await fetchJson(`/api/people/${encodeURIComponent(updated.id || id)}`, { signal: context.abortSignal });
    return {
      ok: true,
      action: 'person_updated',
      person_id: verified.id || updated.id || id,
      display_name: verified.display_name || updated.display_name || '',
      fields: Object.keys(patch),
      verified: true,
      hub_action: { type: 'refresh' },
    };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { ok: false, action: 'person_update_failed', error: err.message || 'Could not update the person.' };
  }
}

async function runDeletePersonTool(args, context = {}) {
  const id = await resolveHubObjectId('people', args.person_id || args.id || args.person, context.pageContext, context.abortSignal);
  if (!id) return { ok: false, action: 'person_delete_failed', error: 'Open a person page or provide a person id/name before deleting.' };
  if (args.confirmed !== true) {
    return {
      ok: false,
      action: 'person_delete_confirmation_required',
      person_id: id,
      error: 'Please confirm the exact person before I delete them.',
    };
  }
  try {
    const deleted = await fetchJson(`/api/people/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      signal: context.abortSignal,
    });
    return {
      ok: true,
      action: 'person_deleted',
      person_id: deleted.id || id,
      verified: true,
      hub_action: { type: 'navigate', path: '/people' },
    };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { ok: false, action: 'person_delete_failed', error: err.message || 'Could not delete the person.' };
  }
}

async function runUpdateEventTool(args, context = {}) {
  const id = await resolveHubObjectId('events', args.event_id || args.id || args.event, context.pageContext, context.abortSignal);
  if (!id) return { ok: false, action: 'event_update_failed', error: 'Open an event page or provide an event id/title before updating.' };
  const patch = patchFromArgs(args, [
    'title', 'status', 'type', 'start', 'end', 'timezone', 'location',
    'virtual_url', 'registration_url', 'capacity', 'description',
  ]);
  if (!Object.keys(patch).length) return { ok: false, action: 'event_update_failed', error: 'No event fields were provided to update.' };
  try {
    const updated = await fetchJson(`/api/events/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
      signal: context.abortSignal,
    });
    const verified = await fetchJson(`/api/events/${encodeURIComponent(updated.id || id)}`, { signal: context.abortSignal });
    return {
      ok: true,
      action: 'event_updated',
      event_id: verified.id || updated.id || id,
      title: verified.title || updated.title || '',
      fields: Object.keys(patch),
      verified: true,
      hub_action: { type: 'refresh' },
    };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { ok: false, action: 'event_update_failed', error: err.message || 'Could not update the event.' };
  }
}

async function runGenerateGalleryImageTool(args, context = {}) {
  const prompt = String(args.prompt || '').trim();
  if (!prompt) return { ok: false, action: 'gallery_image_create_failed', error: 'Image prompt is required.' };
  const hasGeneratedAsset = Boolean(String(args.url || '').trim());
  const body = {
    title: String(args.title || prompt.slice(0, 80) || 'Generated image').trim(),
    folder: String(args.folder || 'Generated').trim(),
    source: hasGeneratedAsset ? 'assistant-generated' : 'assistant-generation-request',
    url: hasGeneratedAsset ? String(args.url).trim() : null,
    description: String(args.description || `Prompt: ${prompt}`).trim(),
    tags: ['assistant', hasGeneratedAsset ? 'generated' : 'generation-request'],
  };
  try {
    const created = await fetchJson('/api/gallery', {
      method: 'POST',
      body: JSON.stringify(body),
      signal: context.abortSignal,
    });
    return {
      ok: true,
      action: hasGeneratedAsset ? 'gallery_image_created' : 'gallery_image_generation_requested',
      id: created.id,
      title: created.title || body.title,
      prompt,
      has_generated_asset: hasGeneratedAsset,
      verified: true,
      hub_action: { type: 'navigate', path: created.id ? `/gallery/${encodeURIComponent(created.id)}` : '/gallery' },
    };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return { ok: false, action: 'gallery_image_create_failed', error: err.message || 'Could not create the gallery record.' };
  }
}

function timeoutSignal(parentSignal, timeoutMs) {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromParent = () => {
    try { controller.abort(parentSignal?.reason); } catch { controller.abort(); }
  };
  if (parentSignal?.aborted) abortFromParent();
  else parentSignal?.addEventListener('abort', abortFromParent, { once: true });

  const timeout = window.setTimeout(() => {
    timedOut = true;
    try { controller.abort(new DOMException('Timed out', 'TimeoutError')); } catch { controller.abort(); }
  }, timeoutMs);

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    cleanup: () => {
      window.clearTimeout(timeout);
      parentSignal?.removeEventListener('abort', abortFromParent);
    },
  };
}

async function callOpenClawTool({ input, pageContext, abortSignal, conversationId, toolArgs }) {
  return {
    ok: false,
    action: 'openclaw_response',
    error: 'OpenClaw handoff is server-side only. Please send the request again.',
  };
}

async function runHubClientTool(call, context = {}) {
  const name = String(call?.name || '').trim();
  const args = parseToolArguments(call?.arguments);
  if (!LOCAL_ORCHESTRATOR_TOOL_NAMES.has(name)) {
    return { ok: false, error: `Unsupported Hub tool: ${name || 'unknown'}` };
  }
  if (name === OPENCLAW_TOOL_NAME) {
    try {
      return await callOpenClawTool({ ...context, toolArgs: args });
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      return {
        ok: false,
        action: 'openclaw_response',
        error: agentConnectionErrorText(),
      };
    }
  }
  if (name === 'hub_refresh_page') {
    return {
      ok: true,
      action: 'refresh_scheduled',
      reason: String(args.reason || ''),
      hub_action: { type: 'refresh' },
    };
  }
  if (name === 'hub_open_section') {
    const section = String(args.section || '').trim().toLowerCase();
    const path = await getHubSectionPath(section, context.abortSignal);
    if (!path) return { ok: false, error: `Unknown Hub section: ${section}` };
    if (!(await isHubPathFeatureEnabled(path, context.abortSignal))) {
      return { ok: false, error: 'That Hub section is disabled.' };
    }
    return {
      ok: true,
      action: 'navigation_scheduled',
      path,
      hub_action: { type: 'navigate', path },
    };
  }
  if (name === 'hub_navigate') {
    const path = await resolveSafeHubPath(args.path, context.abortSignal);
    if (!path) return { ok: false, error: 'Navigation path is not an allowed Hub route.' };
    return {
      ok: true,
      action: 'navigation_scheduled',
      path,
      hub_action: { type: 'navigate', path },
    };
  }
  if (name === 'hub_return_main_menu') {
    return {
      ok: true,
      action: 'main_menu_return_scheduled',
      reason: String(args.reason || ''),
      hub_action: { type: 'return_main_menu' },
    };
  }
  if (name === 'hub_toggle_desktop') {
    return {
      ok: true,
      action: 'desktop_toggle_scheduled',
      reason: String(args.reason || ''),
      hub_action: { type: 'desktop_toggle' },
    };
  }
  if (name === 'hub_open_desktop') {
    return {
      ok: true,
      action: 'desktop_open_scheduled',
      reason: String(args.reason || ''),
      hub_action: { type: 'desktop_open' },
    };
  }
  if (name === 'hub_close_desktop') {
    return {
      ok: true,
      action: 'desktop_close_scheduled',
      reason: String(args.reason || ''),
      hub_action: { type: 'desktop_close' },
    };
  }
  if (name === 'hub_event_open_tab') {
    return runEventOpenTabTool(args);
  }
  if (name === 'hub_event_curate_room') {
    return runEventCurateRoomTool(args, context);
  }
  if (name === 'hub_event_render_curation') {
    return runEventRenderCurationTool(args);
  }
  if (name === 'hub_create_review_item') {
    return runCreateReviewItemTool(args, context);
  }
  if (name === 'hub_open_person') {
    return runOpenPersonTool(args, context);
  }
  if (name === 'hub_update_person') {
    return runUpdatePersonTool(args, context);
  }
  if (name === 'hub_delete_person') {
    return runDeletePersonTool(args, context);
  }
  if (name === 'hub_update_event') {
    return runUpdateEventTool(args, context);
  }
  if (name === 'hub_generate_gallery_image') {
    return runGenerateGalleryImageTool(args, context);
  }
  if (name === 'hub_delete_file_folder') {
    const folder = String(args.folder || '').trim();
    if (!folder) {
      return {
        ok: false,
        action: 'file_folder_delete_failed',
        error: 'Folder path is required.',
      };
    }
    try {
      const r = await fetch('/api/files/folders', {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder, recursive: args.recursive !== false }),
        signal: context.abortSignal,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        return {
          ok: false,
          action: 'file_folder_delete_failed',
          folder,
          error: data.error || `Could not delete ${folder}.`,
        };
      }
      const deletedFolder = data.path || folder;
      return {
        ok: true,
        action: 'file_folder_deleted',
        folder: deletedFolder,
        files_deleted: data.files_deleted || 0,
        file_ids: Array.isArray(data.file_ids) ? data.file_ids : [],
        summary: `Deleted ${deletedFolder} from Hub Files.`,
        hub_action: { type: 'refresh' },
      };
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      return {
        ok: false,
        action: 'file_folder_delete_failed',
        folder,
        error: err.message || `Could not delete ${folder}.`,
      };
    }
  }
  if (name === 'hub_create_scheduled_task' || name === 'hub_create_standing_order') {
    const taskName = String(args.name || '').trim();
    const message = String(args.message || '').trim();
    if (!taskName || !message) return { ok: false, error: 'name and message are required' };
    if (!args.cron && !args.at) return { ok: false, error: 'Provide either `cron` (recurring) or `at` (one-shot).' };
    const body = {
      name: taskName,
      message,
      requireApproval: !!args.requireApproval,
      sendToEmail: args.sendToEmail || null,
    };
    if (args.cron) body.cron = String(args.cron);
    else { body.at = String(args.at); body.deleteAfterRun = true; }
    if (body.requireApproval) body.disabled = true;
    try {
      const r = await fetch('/api/cron', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, error: data.error || `HTTP ${r.status}` };
      return {
        ok: true,
        action: 'scheduled_task_created',
        id: data.id,
        name: data.name,
        enabled: data.enabled,
        schedule: data.schedule,
        nextRunAtMs: data.state && data.state.nextRunAtMs,
        hub_action: { type: 'navigate', path: '/scheduled-tasks' },
      };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }
  return { ok: false, error: 'Unhandled tool.' };
}

function toolOutputForModel(output) {
  if (!output || typeof output !== 'object') return output;
  const { hub_action: _hubAction, ...safeOutput } = output;
  return safeOutput;
}

function normalizeFunctionCall(item) {
  if (!item || item.type !== 'function_call') return null;
  const callId = item.call_id || item.id;
  const name = item.name || item.function?.name;
  const args = item.arguments ?? item.function?.arguments ?? '{}';
  if (!callId || !name) return null;
  return {
    type: 'function_call',
    call_id: String(callId),
    name: String(name),
    arguments: typeof args === 'string' ? args : JSON.stringify(args || {}),
  };
}

function parseResponsesEventBlock(block) {
  let event = 'message';
  let data = '';
  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!data) return { event, data: null, done: false };
  if (data === '[DONE]') return { event, data: null, done: true };
  try { return { event, data: JSON.parse(data), done: false }; } catch {
    return { event, data: null, done: false };
  }
}

async function* readResponsesStream(resp, abortSignal, initialText) {
  if (!resp.body) {
    const data = await resp.json().catch(() => null);
    const text = extractResponsesText(data);
    const calls = extractResponsesFunctionCalls(data);
    yield { text: initialText + text, calls, done: true };
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let acc = initialText;
  const calls = [];
  try {
    while (true) {
      if (abortSignal?.aborted) {
        yield { text: acc, calls, done: false };
        return;
      }
      let chunk;
      try {
        chunk = await readWithIdleTimeout(reader, STREAM_IDLE_TIMEOUT_MS, abortSignal);
      } catch (err) {
        if (err instanceof StreamStalledError) {
          // Abandon the dead socket; the caller resumes from the persisted turn.
          try { await reader.cancel(); } catch {}
        }
        throw err;
      }
      const { value, done } = chunk;
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let idx;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseResponsesEventBlock(block);
        if (parsed.done) {
          yield { text: acc, calls, done: true };
          return;
        }
        if (parsed.event === 'response.failed') {
          const msg = parsed.data?.error?.message || parsed.data?.message || 'Response failed';
          throw new Error(msg);
        }
        if (parsed.event === 'hub.openclaw.status') {
          dispatchOpenClawState(Boolean(parsed.data?.active), parsed.data || {});
        }
        if (parsed.event === 'hub.event.curation.render') {
          scheduleHubAction({
            type: 'event_room_curation_render',
            criteria: parsed.data?.criteria || '',
            event_id: parsed.data?.event_id || '',
            result: parsed.data?.result || null,
          });
        }
        if (parsed.event === 'hub.event.curation.fail') {
          scheduleHubAction({
            type: 'event_room_curation_fail',
            criteria: parsed.data?.criteria || '',
            event_id: parsed.data?.event_id || '',
            error: parsed.data?.error || 'The room curation could not finish.',
          });
        }
        if (parsed.event === 'hub.conversation.updated') {
          const conversationId = responseConversationIdForHandoff(parsed.data?.conversation_id);
          if (conversationId) setStoredConversationId(conversationId);
        }
        if (parsed.event === 'response.output_text.delta') {
          const delta = parsed.data?.delta || parsed.data?.text || parsed.data?.content || '';
          if (typeof delta === 'string' && delta) {
            for (let i = 0; i < delta.length; i += STREAM_REVEAL_CHARS) {
              if (abortSignal?.aborted) {
                yield { text: acc, calls, done: false };
                return;
              }
              acc += delta.slice(i, i + STREAM_REVEAL_CHARS);
              yield { text: acc, calls, done: false };
              if (i + STREAM_REVEAL_CHARS < delta.length) {
                await delay(STREAM_REVEAL_DELAY_MS, abortSignal);
              }
            }
          }
        }
        if (parsed.event === 'response.output_item.done') {
          const call = normalizeFunctionCall(parsed.data?.item || parsed.data?.output_item || parsed.data);
          if (call) calls.push(call);
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch {}
  }
  yield { text: acc, calls, done: true };
}

function extractResponsesText(data) {
  if (!data) return '';
  if (typeof data.output_text === 'string') return data.output_text;
  const output = Array.isArray(data.output) ? data.output : [];
  const parts = [];
  for (const item of output) {
    if (!item || item.type === 'function_call') continue;
    if (typeof item.content === 'string') parts.push(item.content);
    if (Array.isArray(item.content)) {
      for (const part of item.content) {
        if (typeof part?.text === 'string') parts.push(part.text);
        else if (typeof part?.content === 'string') parts.push(part.content);
      }
    }
  }
  return parts.join('');
}

function extractResponsesFunctionCalls(data) {
  const output = Array.isArray(data?.output) ? data.output : [];
  return output.map(normalizeFunctionCall).filter(Boolean);
}

function responseHeadersConversationId(resp) {
  return resp.headers.get('x-moltpod-conversation-id') || resp.headers.get('X-MoltPod-Conversation-Id');
}

function responseConversationIdForHandoff(id) {
  return UUID_RE.test(String(id || '').trim()) ? String(id).trim() : null;
}

function agentConnectionErrorText(reason = 'network') {
  if (reason === 'timeout') {
    return '**Connection problem:** The pod agent handoff did not answer before the timeout, so I stopped waiting. Please send the request again.';
  }
  return '**Connection problem:** The pod agent handoff did not finish. I retried once, but the connection still failed. Please send the request again.';
}

async function postAgentLocalResponses(body, abortSignal) {
  const sentAt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const r = await fetch('/api/agent/local-responses', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: abortSignal,
  });
  try {
    const ttfbMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - sentAt);
    const plannerMs = Number(r.headers.get('X-Hub-Agent-Planner-MS') || '0') || 0;
    const status = r.headers.get('X-Hub-Agent-Planner-Status') || '';
    const route = r.headers.get('X-Hub-Agent-Planner-Route') || '';
    if (plannerMs || status) {
      console.info('[hub-agent.timing]', { planner_ms: plannerMs, ttfb_ms: ttfbMs, status, route });
    }
  } catch {}
  return r;
}

function shouldReportExpiredSessionInlineForCurrentPage() {
  if (typeof window === 'undefined') return false;
  try {
    return /^\/(?:dashboard|chat)?\/?$/.test(window.location.pathname || '/');
  } catch {
    return false;
  }
}

export async function ensureAgentSessionFresh(
  abortSignal,
  { minExpiresInSeconds = 0, redirect = true } = {},
) {
  let resp = null;
  try {
    resp = await fetch('/api/agent/session', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
      signal: abortSignal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') return false;
    return true;
  }

  let data = null;
  try { data = await resp.json(); } catch {}

  if (resp.status === 401) {
    if (redirect) _redirectToLogin(data?.login_url);
    return false;
  }
  if (!resp.ok) return true;

  const expiresIn = Number(data?.expires_in_seconds);
  if (Number.isFinite(expiresIn) && expiresIn <= minExpiresInSeconds) {
    if (redirect) _redirectToLogin(data?.login_url);
    return false;
  }
  return true;
}

function isLocalRouterMiss(resp) {
  return resp.status === 204 || resp.status === 404 || resp.status === 405 || resp.status === 501;
}

function expiredSessionInlineText() {
  return '**Session expired:** Please sign in again, then send your message. I did not start a new login flow automatically.';
}

async function handleAuthOrError(resp, { redirectOnAuth = true } = {}) {
  if (resp.status === 401) {
    let data = null;
    try { data = await resp.json(); } catch {}
    if (redirectOnAuth) {
      _redirectToLogin(data?.login_url);
      return 'Session expired — redirecting to sign in…';
    }
    return expiredSessionInlineText();
  }
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    return `**Upstream error ${resp.status}:** ${txt.slice(0, 400)}`;
  }
  return null;
}

async function* runResponses({ messages, abortSignal, pageContext }) {
  const conv = responseConversationIdForHandoff(getStoredConversationId());
  // When the server already owns the conversation thread, do NOT prepend the
  // sessionStorage cache — the server-side thread is authoritative and merging
  // produces duplicate entries on reload after a hard navigation.
  const responseInput = messagesToResponseInput(messages);
  const baseInput = conv ? responseInput : mergeStoredThreadIntoInput(responseInput);
  let input = [...baseInput];
  let acc = '';
  const toolCallCounts = new Map();

  for (let step = 0; step < MAX_TOOL_STEPS; step += 1) {
    const reportExpiredSessionInline = shouldReportExpiredSessionInlineForCurrentPage();
    const body = {
      model: 'openclaw:main',
      input,
      instructions: pageContext?.prompt && isContextForCurrentPage(pageContext) ? pageContext.prompt : undefined,
      tools: localOrchestratorTools,
      tool_choice: 'auto',
      stream: true,
      user: conv || 'hub-agent',
    };
    if (conv) body.conversation_id = conv;

    const sessionOk = await ensureAgentSessionFresh(abortSignal, {
      minExpiresInSeconds: AGENT_SESSION_REFRESH_THRESHOLD_SECONDS,
      redirect: !reportExpiredSessionInline,
    });
    if (!sessionOk) {
      if (reportExpiredSessionInline) {
        yield { content: [{ type: 'text', text: expiredSessionInlineText() }] };
      }
      return;
    }

    let resp = null;
    try {
      resp = await postAgentLocalResponses(body, abortSignal);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      yield { content: [{ type: 'text', text: '**Connection problem:** The local Hub assistant could not start this turn. Please send the request again.' }] };
      return;
    }

    if (isLocalRouterMiss(resp)) {
      yield { content: [{ type: 'text', text: '**Routing problem:** The local Hub assistant could not decide how to handle that request. Please send it again with a little more detail.' }] };
      return;
    }

    const errorText = await handleAuthOrError(resp, { redirectOnAuth: !reportExpiredSessionInline });
    if (errorText) {
      yield { content: [{ type: 'text', text: errorText }] };
      return;
    }

    const newConv = responseHeadersConversationId(resp);
    if (newConv) setStoredConversationId(newConv);

    let result = { text: acc, calls: [], done: false };
    try {
      for await (const event of readResponsesStream(resp, abortSignal, acc)) {
        result = event;
        acc = event.text || acc;
        if (acc) yield { content: [{ type: 'text', text: acc }] };
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (err?.name === 'StreamStalledError') {
        // The live stream went silent. Rather than force a resend, reconnect and
        // poll the persisted conversation for the finished reply — the server
        // keeps working and stores the turn even though this socket died.
        yield { content: [{ type: 'text', text: `${acc ? acc + '\n\n' : ''}_Reconnecting…_`.trim() }] };
        const recovered = yield* resumeStalledResponse(getStoredConversationId() || conv, acc, abortSignal);
        if (abortSignal?.aborted) return;
        if (recovered?.text) {
          acc = recovered.text;
          yield { content: [{ type: 'text', text: acc }] };
          return;
        }
        yield { content: [{ type: 'text', text: `${acc ? acc + '\n\n' : ''}**Connection interrupted:** I lost the live stream and could not recover the reply automatically. Send any message to resync this conversation.`.trim() }] };
        return;
      }
      if (!acc) {
        try {
          const fallbackResp = await postAgentLocalResponses({ ...body, stream: false }, abortSignal);
          const fallbackError = await handleAuthOrError(fallbackResp, { redirectOnAuth: !reportExpiredSessionInline });
          if (fallbackError) {
            yield { content: [{ type: 'text', text: fallbackError }] };
            return;
          }
          const data = await fallbackResp.json().catch(() => null);
          result = {
            text: extractResponsesText(data),
            calls: extractResponsesFunctionCalls(data),
            done: true,
          };
          acc = result.text;
          if (acc) yield { content: [{ type: 'text', text: acc }] };
        } catch (fallbackErr) {
          if (fallbackErr?.name === 'AbortError') return;
          yield { content: [{ type: 'text', text: '**Connection problem:** The local Hub assistant stream stopped before it returned text. Please send the request again.' }] };
          return;
        }
      } else {
        yield { content: [{ type: 'text', text: `${acc}\n\n**Connection interrupted:** The local Hub assistant stream stopped before it finished. Please send the request again.`.trim() }] };
        return;
      }
    }

    acc = result.text;
    if (!result.calls.length) {
      rememberAssistantTurn(baseInput, acc);
      return;
    }

    for (const call of result.calls) {
      const callSignature = `${call.name}:${stableToolArguments(call.arguments)}`;
      const callCount = (toolCallCounts.get(callSignature) || 0) + 1;
      toolCallCounts.set(callSignature, callCount);
      if (callCount > MAX_REPEATED_TOOL_CALLS) {
        const text = acc || 'I stopped because the same Hub tool was requested repeatedly. Please send the request again with a narrower instruction.';
        rememberAssistantTurn(baseInput, text);
        yield {
          content: [{
            type: 'text',
            text,
          }],
        };
        return;
      }
      if (isPlannerMissOpenClawCall(call)) {
        const text = acc || 'I lost the routing decision before I could safely act, so I stopped that handoff instead of letting the chat hang. Please send the request again.';
        rememberAssistantTurn(baseInput, text);
        yield {
          content: [{
            type: 'text',
            text,
          }],
        };
        return;
      }
      const visible = acc || (call.name === 'hub_event_curate_room'
        ? 'I’m opening the guest list and curating the room from the event data.'
        : '');
      if (visible) yield { content: [{ type: 'text', text: visible }] };
      let output;
      try {
        output = await runHubClientTool(call, { input: baseInput, pageContext, abortSignal, conversationId: conv });
      } catch (err) {
        if (err?.name === 'AbortError') return;
        output = {
          ok: false,
          action: call.name === 'hub_event_render_curation'
            ? 'event_room_curation_failed'
            : 'hub_tool_failed',
          error: err?.message || 'The Hub tool failed before it could update the page.',
        };
      }
      if (output?.hub_action) scheduleHubAction(output.hub_action);
      const terminalText = terminalHubToolText(output);
      if (terminalText) {
        if (!acc) yield { content: [{ type: 'text', text: terminalText }] };
        rememberAssistantTurn(baseInput, acc || terminalText);
        return;
      }
      if (call.name === 'hub_event_render_curation') {
        const text = acc || output?.summary || output?.error || 'Done. I updated the curated guest view.';
        if (text) yield { content: [{ type: 'text', text }] };
        rememberAssistantTurn(baseInput, text);
        return;
      }
      input.push(call, {
        type: 'function_call_output',
        call_id: call.call_id,
        output: JSON.stringify(toolOutputForModel(output)),
      });
    }
  }

  const limitText = `${acc}${acc ? '\n\n' : ''}**Tool limit reached:** I stopped after ${MAX_TOOL_STEPS} Hub tool steps.`;
  rememberAssistantTurn(baseInput, limitText);
  yield {
    content: [{
      type: 'text',
      text: limitText,
    }],
  };
}

export const podAdapter = {
  async *run({ messages, abortSignal }) {
    async function* runWithContext() {
      let pageContext = getCurrentHubPageContext();
      if (!isContextForCurrentPage(pageContext)) {
        pageContext = await collectHubPageContext(abortSignal);
        setCurrentHubPageContext(pageContext);
      }
      yield* runResponses({ messages, abortSignal, pageContext });
    }

    dispatchAssistantRunState(true);
    try {
      yield* withPendingResponseFrames(runWithContext(), abortSignal);
    } finally {
      dispatchOpenClawState(false, { phase: 'cleanup' });
      dispatchAssistantRunState(false);
    }
  },
};
